import { useMemo } from 'react';
import { usePermissions } from '@/features/auth';
import { getProductCurrency } from '@/features/products/services/productCurrency';
import type { Product, SchedulingMethod } from '@/features/products/types/products.types';
import type { ConfiguredService, ConfiguredAddOn } from '@/features/service-plans/types/services.types';
import type { BasketPricingState } from '../../hooks/useBasketPricing';
import type { AppliedDeposit } from '../../services/appliedDeposit';
import type { PaymentSelection, SchedulePreview } from '../../types/schedule.types';
import { isValidPaymentSelection } from '../../types/schedule.types';
import { confirmedDepositMinor, EVEN_MONTHLY_FAMILY_KEYS } from '../../services/schedulePreview';
import { collectionCountChoice, transmittedCollectionCount } from '../../services/collectionCount';
import { toBasketGross } from '../../services/basketGross';
import { earliestPricedDueDate, isPastDueForScheduling } from '../../services/pastDueScheduling';
import { decimalToNumber } from '../../utils/decimalMoney';
import { mergePricedLines } from '../../utils/pricedLines';
import { resolveDefaultMethodKey } from '../steps/PaymentStep/derive';
import { useDepositDerivation } from '../steps/PaymentStep/useDepositDerivation';
import { useScheduleDerivation } from '../steps/PaymentStep/useScheduleDerivation';
import { useSchedulePreviewParams } from '../steps/PaymentStep/useSchedulePreviewParams';
import { shouldDiscardProductDeposit } from '../steps/PaymentStep/depositMode';
import { deriveDepositBounds } from '../steps/PaymentStep/depositBounds';
import { useProductDeposit } from '../steps/PaymentStep/usePaymentDeposit';
import { SCHEDULE_PREVIEW_PERMISSION } from '../steps/PaymentStep/SchedulePreviewPane';

/**
 * The Configure page's read-only schedule digest. It re-prices against the CURRENT basket — the
 * confirmed selection's terms, or the product default — never a stored snapshot.
 */
export function useDefaultScheduleSummary(options: {
    product: Product | null | undefined;
    configuredServices: ConfiguredService[];
    configuredAddOns: ConfiguredAddOn[];
    pricing: BasketPricingState;
    paymentSelection?: PaymentSelection;
    /** True while an upstream regime recompute (e.g. SMRiQ "Updating plan…") is in flight. */
    isServiceOrderUpdating?: boolean;
}): {
    method: SchedulingMethod | null;
    data: SchedulePreview | undefined;
    appliedDeposit: AppliedDeposit | undefined;
    currency: string;
    anchor: string | undefined;
    isLoading: boolean;
} {
    const { product, configuredServices, configuredAddOns, pricing, paymentSelection } = options;
    const isServiceOrderUpdating = options.isServiceOrderUpdating === true;
    const confirmed = isValidPaymentSelection(paymentSelection) ? paymentSelection : null;
    const { hasPermission } = usePermissions();

    const methods = product?.schedulingMethods ?? [];
    const methodKey = confirmed?.methodKey ?? resolveDefaultMethodKey(product, configuredServices, configuredAddOns);
    const method = methods.find((m) => m.key === methodKey) ?? null;

    const productDeposit = useProductDeposit(product);
    const depositForDerivation = confirmed
        ? confirmedDepositMinor(confirmed.params)
        : productDeposit?.amountMinor;

    const { isDepositBearing, isItemBasedFamily, isItemBasedDepositBearing, depositMode, effectiveDepositMinor } =
        useDepositDerivation(
        method,
        product,
        depositForDerivation,
    );
    const isEvenMonthlyFamily = EVEN_MONTHLY_FAMILY_KEYS.has(method?.key ?? '');

    const leadMonths = product?.collectionLeadMonths ?? 0;
    const effectiveLeadMonths = confirmed?.collectionLeadMonths ?? leadMonths;
    const currency = getProductCurrency(product);
    const canPreviewSchedule = hasPermission(SCHEDULE_PREVIEW_PERMISSION);

    const merged = useMemo(
        () => mergePricedLines(configuredServices, configuredAddOns, pricing.data),
        [configuredServices, configuredAddOns, pricing.data],
    );
    const basketGross = useMemo(() => toBasketGross(pricing, merged), [pricing, merged]);
    const basketGrossMinor = basketGross.kind === 'derived' ? basketGross.amountMinor : 0;

    const { itemBased, evenMonthly, anchor } = useScheduleDerivation({
        isItemBasedFamily,
        leadMonths,
        effectiveLeadMonths,
        minInstalments: product?.minCollectionInstalments,
        defaultInstalments: product?.defaultCollectionInstalments,
        selectedMethodKey: methodKey,
        depositMinor: effectiveDepositMinor,
        configuredServices: merged.services,
        configuredAddOns: merged.addOns,
        feeGross: pricing.data ? decimalToNumber(pricing.data.fees.gross) : 0,
        schedulingMethods: product?.schedulingMethods,
    });
    const { targets: collectionTargets, numInstalments: derivedNumInstalments } = itemBased;

    const bounds = deriveDepositBounds({
        isDepositBearingMethod: isDepositBearing,
        isItemBasedDepositBearing,
        isItemBasedFamily,
        isDepositFixed: depositMode.kind === 'fixed',
        collectionTargets,
        basketGrossMinor,
        maxDepositAmountMinor: product?.maxDepositAmountMinor,
        depositMinor: effectiveDepositMinor,
    });
    const discardsProductDeposit =
        confirmed === null &&
        depositMode.kind !== 'fixed' &&
        shouldDiscardProductDeposit({
            isReadOnly: false,
            isDepositBearing,
            isBoundsSettled:
                basketGross.kind === 'derived' &&
                (!isItemBasedDepositBearing || bounds.firstTargetMinor !== undefined),
            origin: 'product',
            depositMinor: depositForDerivation,
            effectiveMaxExclusive: bounds.effectiveMaxExclusive,
        });
    const previewDepositMinor = discardsProductDeposit ? undefined : effectiveDepositMinor;

    // Configure can shorten the regime after the count was confirmed, so the persisted value is
    // re-checked against today's range — out of range falls back to the clamped preselect.
    const collectionChoice = collectionCountChoice({
        allowChoice: isEvenMonthlyFamily && product?.allowCollectionInstalmentChoice === true,
        termMonths: evenMonthly.kind === 'offerable' ? evenMonthly.termMonths : undefined,
        leadMonths: effectiveLeadMonths,
        derivedCount: evenMonthly.kind === 'offerable' ? evenMonthly.numInstalments : 0,
        minInstalments: product?.minCollectionInstalments,
        maxInstalments: product?.maxCollectionInstalments,
        depositConsumesInstalment: isDepositBearing && previewDepositMinor !== 0,
    });
    const transmittedInstalments = transmittedCollectionCount(
        collectionChoice,
        confirmed?.params.numInstalments,
    );

    const pastDueForScheduling = isPastDueForScheduling({
        earliestDue: earliestPricedDueDate(merged.services, merged.addOns),
        anchor,
    });

    const { data, appliedDeposit, isLoading: previewLoading, isFetching: previewFetching } =
        useSchedulePreviewParams({
            isReadOnly: false,
            canPreviewSchedule,
            selectedMethod: method,
            basketGrossMinor,
            isDepositBearing,
            depositMinor: previewDepositMinor,
            isItemBasedFamily,
            collectionTargets,
            derivedNumInstalments,
            // The digest never gates on bounds — a confirmed deposit/lead that no longer fits the
            // current basket simply yields no preview (the card then shows nothing).
            depositExceedsEffectiveMax: false,
            leadOutOfBounds: false,
            pastDueForScheduling,
            isEvenMonthlyFamily,
            evenMonthly,
            // The confirmed even-monthly count, re-applied to today's term (ignored for other families).
            evenMonthlyInstalmentsOverride: transmittedInstalments,
            anchor,
        });

    // The spinner overrides any stale prior schedule while the regime is being (re)built, so the
    // card never lingers on out-of-date figures.
    const isLoading =
        isServiceOrderUpdating ||
        (method !== null &&
            (basketGross.kind === 'pending' ||
                pricing.isUpdating ||
                previewLoading ||
                previewFetching));

    return { method, data, appliedDeposit, currency, anchor, isLoading };
}
